import { readSheet } from 'read-excel-file/browser';

import {
  DEAL_OPTIONS_SHEETS,
  INCLUSIONS_SHEET,
  V4_PRICING_SHEET,
  V5_PRICING_SHEET,
  parseWorkbook,
} from './parseWorkbook';
import type { ParsedWorkbook, SheetRows } from './types';

const SHEET_NOT_FOUND = /^Sheet ".*" not found/;

// Only a missing sheet means "try the other template"; any other error is a file we cannot read.
async function readIfPresent(file: File, sheet: string): Promise<SheetRows | null> {
  try {
    return await readSheet(file, sheet);
  } catch (error) {
    if (error instanceof Error && SHEET_NOT_FOUND.test(error.message)) {
      return null;
    }
    throw error;
  }
}

async function readFirstPresent(file: File, candidates: Array<string>): Promise<Record<string, SheetRows>> {
  for (const sheet of candidates) {
    const rows = await readIfPresent(file, sheet);
    if (rows) {
      return { [sheet]: rows };
    }
  }
  return {};
}

// One sheet at a time: reading the whole workbook parses sheets 16k columns wide.
export async function readWorkbook(file: File): Promise<ParsedWorkbook> {
  const pricing = await readFirstPresent(file, [V5_PRICING_SHEET, V4_PRICING_SHEET]);
  if (Object.keys(pricing).length === 0) {
    return parseWorkbook({});
  }
  // Every miss unzips the whole file again, so a v4 file starts with the name v4 uses.
  const dealOptionsSheets = pricing[V4_PRICING_SHEET] ? [...DEAL_OPTIONS_SHEETS].reverse() : DEAL_OPTIONS_SHEETS;
  const dealOptions = await readFirstPresent(file, dealOptionsSheets);
  const inclusions = await readFirstPresent(file, [INCLUSIONS_SHEET]);
  return parseWorkbook({ ...pricing, ...dealOptions, ...inclusions });
}
